import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api/client';
import {
  useProvisionUserMutation,
  useInviteViewerMutation,
  type InviteViewerResult,
} from '@/lib/api/settings';
import { useCreateClientMutation } from '@/lib/api/clients';
import { useClientsQuery } from '@/lib/api/clients';
import { useEngagementsListQuery } from '@/lib/api/engagements';
import { cn } from '@/lib/utils/cn';

/**
 * Directory create modals (verification pass). The Invite-user,
 * Invite-viewer, and New-client buttons all stamped `data-create` kinds
 * the modal host never handled — enabled-looking primary CTAs that did
 * nothing, with their mutations already built and orphaned. These small
 * local modals give each its real form.
 */

const inputCls =
  'mt-1 w-full rounded-card border border-ink-200 bg-paper-0 px-2 py-1.5 text-body-sm text-ink-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-ink-300';
const invalidInputCls = 'border-posture-critical';

// The same syntactic check the API applies (`EMAIL_RE` in services/clients.ts).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The message under a New client field, or null when the value is fine. */
export function newClientFieldProblem(
  field: 'name' | 'contact_name' | 'contact_email',
  value: string,
): string | null {
  const v = value.trim();
  if (field === 'name') return v.length === 0 ? 'Client name is required.' : null;
  if (field === 'contact_name') return v.length === 0 ? 'Primary contact is required.' : null;
  return EMAIL_RE.test(v) ? null : 'Enter a valid email.';
}

/** Inline problem under a field (`.field-help` in error colour, announced). */
function FieldProblem({ id, message }: { id: string; message: string | null }) {
  if (!message) return null;
  return (
    <span id={id} role="alert" className="mt-1 block text-meta font-normal text-posture-critical">
      {message}
    </span>
  );
}

function Shell({
  title,
  subtitle,
  open,
  onOpenChange,
  children,
  footer,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-scrim" />
        <Dialog.Content
          aria-describedby={undefined}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[min(480px,94vw)] max-h-[90vh] -translate-x-1/2 -translate-y-1/2',
            'grid grid-rows-[auto,1fr,auto] overflow-hidden rounded-modal bg-paper-0 shadow-modal',
          )}
        >
          <div className="flex items-start justify-between gap-3 border-b border-ink-100 bg-paper-50 px-5 py-4">
            <div>
              <Dialog.Title className="text-h2 font-semibold text-ink-900">{title}</Dialog.Title>
              {subtitle ? <p className="mt-1 text-meta text-ink-500">{subtitle}</p> : null}
            </div>
            <Dialog.Close
              aria-label="Close"
              className="inline-flex h-8 w-8 items-center justify-center rounded-card text-ink-500 hover:bg-paper-100 hover:text-ink-900"
            >
              <X className="h-4 w-4" aria-hidden="true" />
            </Dialog.Close>
          </div>
          <div className="space-y-4 overflow-y-auto px-5 py-4">{children}</div>
          <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-paper-50 px-5 py-3">
            {footer}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function InviteUserModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'firm_admin' | 'engagement_lead' | 'consultant'>('consultant');
  const [error, setError] = useState<string | null>(null);
  const provision = useProvisionUserMutation();

  return (
    <Shell
      title="Invite firm user"
      subtitle="Provisions the account; they sign in with Microsoft Entra SSO."
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="sm">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="button"
            size="sm"
            disabled={!email.includes('@') || name.trim().length === 0 || provision.isPending}
            onClick={() => {
              setError(null);
              provision.mutate(
                { email: email.trim(), name: name.trim(), default_role: role },
                {
                  onSuccess: () => {
                    setEmail('');
                    setName('');
                    onOpenChange(false);
                  },
                  onError: (e) =>
                    setError(e instanceof ApiError ? `${e.code}: ${e.message}` : 'Invite failed.'),
                },
              );
            }}
          >
            {provision.isPending ? <Spinner /> : null}
            Provision user
          </Button>
        </>
      }
    >
      <label className="block text-meta font-medium text-ink-700">
        Work email
        <input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
      </label>
      <label className="block text-meta font-medium text-ink-700">
        Display name
        <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
      </label>
      <label className="block text-meta font-medium text-ink-700">
        Role
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as typeof role)}
          className={inputCls}
        >
          <option value="consultant">Consultant</option>
          <option value="engagement_lead">Engagement lead</option>
          <option value="firm_admin">Firm admin</option>
        </select>
      </label>
      {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
    </Shell>
  );
}

export function InviteViewerModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [authMode, setAuthMode] = useState<'magic_link' | 'sso'>('magic_link');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<InviteViewerResult | null>(null);
  const invite = useInviteViewerMutation();
  const clients = useClientsQuery();
  const engagements = useEngagementsListQuery();

  const clientEngagements = (engagements.data?.engagements ?? []).filter(
    (e) => e.client_id === clientId,
  );

  return (
    <Shell
      title="Invite client viewer"
      subtitle="Read-only access. The invitation email carries a single-use secure link."
      open={open}
      onOpenChange={(next) => {
        if (!next) setResult(null);
        onOpenChange(next);
      }}
      footer={
        <>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="sm">
              {result ? 'Done' : 'Cancel'}
            </Button>
          </Dialog.Close>
          {!result ? (
            <Button
              type="button"
              size="sm"
              disabled={
                !email.includes('@') || name.trim().length === 0 || !clientId || invite.isPending
              }
              onClick={() => {
                setError(null);
                invite.mutate(
                  {
                    viewer_email: email.trim(),
                    viewer_name: name.trim(),
                    client_id: clientId,
                    project_id: projectId || null,
                    auth_mode: authMode,
                  },
                  {
                    onSuccess: (r) => setResult(r),
                    onError: (e) =>
                      setError(
                        e instanceof ApiError ? `${e.code}: ${e.message}` : 'Invite failed.',
                      ),
                  },
                );
              }}
            >
              {invite.isPending ? <Spinner /> : null}
              Send invitation
            </Button>
          ) : null}
        </>
      }
    >
      {result ? (
        <p className="rounded-card border border-posture-healthy bg-paper-50 p-3 text-body-sm text-ink-700">
          Invitation created for <strong>{email}</strong>. The secure link was emailed
          {result.invite_url ? ' (and is available below for manual sharing)' : ''}.
          {result.invite_url ? (
            <span className="mt-2 block break-all font-mono text-meta text-ink-500">
              {result.invite_url}
            </span>
          ) : null}
        </p>
      ) : (
        <>
          <label className="block text-meta font-medium text-ink-700">
            Viewer email
            <input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-meta font-medium text-ink-700">
            Viewer name
            <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
          </label>
          <label className="block text-meta font-medium text-ink-700">
            Client
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setProjectId('');
              }}
              className={inputCls}
            >
              <option value="">— select a client —</option>
              {(clients.data ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-meta font-medium text-ink-700">
            Engagement (optional — scopes the grant)
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className={inputCls}
              disabled={!clientId}
            >
              <option value="">All client engagements</option>
              {clientEngagements.map((e) => (
                <option key={e.project_id} value={e.project_id}>
                  {e.name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-meta font-medium text-ink-700">
            Access mode
            <select
              value={authMode}
              onChange={(e) => setAuthMode(e.target.value as typeof authMode)}
              className={inputCls}
            >
              <option value="magic_link">Secure link (no account needed)</option>
              <option value="sso">Their own Microsoft sign-in</option>
            </select>
          </label>
          {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
        </>
      )}
    </Shell>
  );
}

/**
 * New client (C2-34): a problem with a value shows under its field as soon
 * as the field is left or Create is tried, instead of a button that is
 * silently disabled. The server applies the same checks and is the final
 * word; the messages here only save a round trip.
 */
export function CreateClientModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [clientType, setClientType] = useState('city');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [touched, setTouched] = useState<{ name?: boolean; contact?: boolean; email?: boolean }>(
    {},
  );
  const [tried, setTried] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = useCreateClientMutation();

  const problems = {
    name: newClientFieldProblem('name', name),
    contact: newClientFieldProblem('contact_name', contactName),
    email: newClientFieldProblem('contact_email', contactEmail),
  };
  const shown = (field: keyof typeof problems) =>
    tried || touched[field] ? problems[field] : null;
  const hasProblem = Object.values(problems).some((p) => p !== null);

  return (
    <Shell
      title="New client"
      subtitle="Creates the client record; engagements attach to it from the wizard."
      open={open}
      onOpenChange={onOpenChange}
      footer={
        <>
          <Dialog.Close asChild>
            <Button type="button" variant="ghost" size="sm">
              Cancel
            </Button>
          </Dialog.Close>
          <Button
            type="button"
            size="sm"
            disabled={create.isPending}
            onClick={() => {
              setTried(true);
              if (hasProblem) return;
              setError(null);
              create.mutate(
                {
                  input: {
                    name: name.trim(),
                    client_type: clientType,
                    primary_contact_name: contactName.trim(),
                    primary_contact_email: contactEmail.trim(),
                  },
                  idempotencyKey: crypto.randomUUID(),
                },
                {
                  onSuccess: () => {
                    setName('');
                    setContactName('');
                    setContactEmail('');
                    setTouched({});
                    setTried(false);
                    onOpenChange(false);
                  },
                  onError: (e) =>
                    setError(e instanceof ApiError ? `${e.code}: ${e.message}` : 'Create failed.'),
                },
              );
            }}
          >
            {create.isPending ? <Spinner /> : null}
            Create client
          </Button>
        </>
      }
    >
      <label className="block text-meta font-medium text-ink-700">
        Client name
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, name: true }))}
          aria-invalid={shown('name') ? true : undefined}
          aria-describedby={shown('name') ? 'new-client-name-problem' : undefined}
          className={cn(inputCls, shown('name') && invalidInputCls)}
        />
        <FieldProblem id="new-client-name-problem" message={shown('name')} />
      </label>
      <label className="block text-meta font-medium text-ink-700">
        Type
        <select
          value={clientType}
          onChange={(e) => setClientType(e.target.value)}
          className={inputCls}
        >
          <option value="city">City</option>
          <option value="county">County</option>
          <option value="state_agency">State agency</option>
          <option value="other">Other</option>
        </select>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block text-meta font-medium text-ink-700">
          Primary contact
          <input
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, contact: true }))}
            aria-invalid={shown('contact') ? true : undefined}
            aria-describedby={shown('contact') ? 'new-client-contact-problem' : undefined}
            className={cn(inputCls, shown('contact') && invalidInputCls)}
          />
          <FieldProblem id="new-client-contact-problem" message={shown('contact')} />
        </label>
        <label className="block text-meta font-medium text-ink-700">
          Contact email
          <input
            type="email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            aria-invalid={shown('email') ? true : undefined}
            aria-describedby={shown('email') ? 'new-client-email-problem' : undefined}
            className={cn(inputCls, shown('email') && invalidInputCls)}
          />
          <FieldProblem id="new-client-email-problem" message={shown('email')} />
        </label>
      </div>
      {error ? <p className="text-meta font-medium text-cite-broken">{error}</p> : null}
    </Shell>
  );
}
